// ==================== CONTACT FORM MODAL ====================

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import {
  X,
  Phone,
  User,
  Mail,
  Building,
  FileText,
  Tag,
  FolderPlus,
  Loader2,
  Plus,
  Check,
  Settings2,
  Calendar,
  Link,
  Hash,
} from 'lucide-react';
import { Button, Input, Modal, Checkbox, Select } from '../../../components/ui';
import type { SelectOption } from '../../../components/ui/Select';
import { contactsService } from '../../../services/contacts.service';
import type { Contact, ContactGroup, ContactCustomField } from '../../../types';
import { COUNTRIES } from '../../../constants/countries.constants';

// Convert countries to Select options
const countryOptions: SelectOption[] = COUNTRIES.map((country) => ({
  value: country.code,
  label: `${country.flag} ${country.name}`,
  icon: <span className="text-lg">{country.code}</span>,
}));

// ==================== VALIDATION SCHEMA ====================

const contactSchema = z.object({
  phoneNumber: z
    .string()
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number is too long')
    .regex(/^[\d+\-\s()]+$/, 'Invalid phone number format'),
  name: z.string().max(100, 'Name is too long').optional().or(z.literal('')),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  company: z.string().max(100, 'Company name is too long').optional().or(z.literal('')),
  country: z.string().max(3, 'Country code is too long').optional().or(z.literal('')),
  notes: z.string().max(500, 'Notes are too long').optional().or(z.literal('')),
  optedIn: z.boolean().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

// ==================== PROPS ====================

interface ContactFormModalProps {
  contact?: Contact | null;
  groups: ContactGroup[];
  onClose: () => void;
  onSuccess: () => void;
}

// ==================== COMPONENT ====================

const ContactFormModal = ({
  contact,
  groups,
  onClose,
  onSuccess,
}: ContactFormModalProps) => {
  const isEditing = !!contact;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Tags state
  const [tags, setTags] = useState<string[]>(contact?.tags || []);
  const [tagInput, setTagInput] = useState('');
  
  // Groups state
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    contact?.groups?.map((g) => g.group.id) || []
  );

  // Country state
  const [selectedCountry, setSelectedCountry] = useState<string>(contact?.country || '');

  // Custom fields state
  const [customFields, setCustomFields] = useState<ContactCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>(
    contact?.customFields || {}
  );
  const [loadingCustomFields, setLoadingCustomFields] = useState(true);
  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      phoneNumber: contact?.phoneNumber || '',
      name: contact?.name || '',
      email: contact?.email || '',
      company: contact?.company || '',
      country: contact?.country || '',
      notes: contact?.notes || '',
      optedIn: contact?.optedIn ?? true,
    },
  });

  const optedInValue = watch('optedIn');

  // Fetch custom fields on mount
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const fields = await contactsService.getCustomFields();
        setCustomFields(fields || []);
      } catch (err) {
        console.error('Failed to fetch custom fields:', err);
      } finally {
        setLoadingCustomFields(false);
      }
    };
    fetchCustomFields();
  }, []);

  useEffect(() => {
    if (contact) {
      reset({
        phoneNumber: contact.phoneNumber,
        name: contact.name || '',
        email: contact.email || '',
        company: contact.company || '',
        country: contact.country || '',
        notes: contact.notes || '',
        optedIn: contact.optedIn,
      });
      setTags(contact.tags || []);
      setSelectedGroupIds(contact.groups?.map((g) => g.group.id) || []);
      setSelectedCountry(contact.country || '');
      setCustomFieldValues(contact.customFields || {});
    }
  }, [contact, reset]);

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setValue('country', countryCode);
  };

  // Handle custom field value change
  const handleCustomFieldChange = (fieldName: string, value: unknown) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    
    // Clear error for this field when user provides a value
    if (customFieldErrors[fieldName]) {
      const hasValue = value !== undefined && value !== null && value !== '' && 
        !(Array.isArray(value) && value.length === 0);
      
      if (hasValue) {
        setCustomFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    }
  };

  // Validate required custom fields
  const validateRequiredCustomFields = (): string[] => {
    const missingFields: string[] = [];
    const errors: Record<string, string> = {};
    
    for (const field of customFields) {
      if (field.isRequired) {
        const value = customFieldValues[field.name];
        
        // Check if the value is empty/missing
        const isEmpty = 
          value === undefined || 
          value === null || 
          value === '' ||
          (Array.isArray(value) && value.length === 0);
        
        if (isEmpty) {
          missingFields.push(field.label);
          errors[field.name] = `${field.label} is required`;
        }
      }
    }
    
    setCustomFieldErrors(errors);
    return missingFields;
  };

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setError(null);

    // Validate required custom fields
    const missingRequiredFields = validateRequiredCustomFields();
    if (missingRequiredFields.length > 0) {
      setError(`The following required fields are missing: ${missingRequiredFields.join(', ')}`);
      setIsSubmitting(false);
      return;
    }

    try {
      // Filter out empty custom field values
      const filteredCustomFields = Object.fromEntries(
        Object.entries(customFieldValues).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );

      const payload = {
        phoneNumber: data.phoneNumber,
        name: data.name || undefined,
        email: data.email || undefined,
        company: data.company || undefined,
        country: selectedCountry || undefined,
        notes: data.notes || undefined,
        optedIn: data.optedIn,
        tags,
        groupIds: selectedGroupIds,
        customFields: Object.keys(filteredCustomFields).length > 0 ? filteredCustomFields : undefined,
      };

      if (isEditing && contact) {
        await contactsService.update(contact.id, payload);
      } else {
        await contactsService.create(payload);
      }

      onSuccess();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(prev => prev.filter((t) => t !== tag));
  };

  const handleToggleGroup = (groupId: string) => {
    setSelectedGroupIds(prev => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  return (
    <Modal 
      isOpen 
      onClose={onClose} 
      size="lg"
      title={isEditing ? 'Edit Contact' : 'Add New Contact'}
      showCloseButton
    >
      <div className="p-6">
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm"
          >
            {error}
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Phone Number <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                {...register('phoneNumber')}
                type="tel"
                placeholder="+1 (555) 123-4567"
                className="pl-10"
                error={errors.phoneNumber?.message}
              />
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                {...register('name')}
                type="text"
                placeholder="John Doe"
                className="pl-10"
                error={errors.name?.message}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                {...register('email')}
                type="email"
                placeholder="john@example.com"
                className="pl-10"
                error={errors.email?.message}
              />
            </div>
          </div>

          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Company
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                {...register('company')}
                type="text"
                placeholder="Acme Inc."
                className="pl-10"
                error={errors.company?.message}
              />
            </div>
          </div>

          {/* Country */}
          <div>
            <Select
              label="Country"
              options={countryOptions}
              value={selectedCountry}
              onChange={(value) => handleCountrySelect(value as string)}
              placeholder="Select country..."
              searchable
              clearable
              maxHeight={300}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Notes
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <textarea
                {...register('notes')}
                placeholder="Add notes about this contact..."
                rows={3}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
              />
            </div>
            {errors.notes && (
              <p className="mt-1 text-sm text-red-400">{errors.notes.message}</p>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Tag className="inline w-4 h-4 mr-1" />
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag) => (
                <motion.span
                  key={tag}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-500/20 text-primary-300 rounded-full text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-white transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddTag}
                disabled={!tagInput.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Groups */}
          {groups.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FolderPlus className="inline w-4 h-4 mr-1" />
                Groups
              </label>
              <div className="flex flex-wrap gap-2 p-3 bg-white/5 rounded-lg max-h-32 overflow-y-auto">
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => handleToggleGroup(group.id)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                      selectedGroupIds.includes(group.id)
                        ? 'bg-primary-500 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                    style={{
                      backgroundColor: selectedGroupIds.includes(group.id)
                        ? group.color || undefined
                        : undefined,
                    }}
                  >
                    {selectedGroupIds.includes(group.id) && (
                      <Check className="w-3 h-3" />
                    )}
                    {group.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Fields */}
          {!loadingCustomFields && customFields.length > 0 && (
            <div className="border-t border-white/10 pt-5">
              <label className="block text-sm font-medium text-gray-300 mb-4">
                <Settings2 className="inline w-4 h-4 mr-1" />
                Custom Fields
              </label>
              <div className="space-y-4">
                {customFields
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((field) => (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        {field.label}
                        {field.isRequired && <span className="text-red-400 ml-1">*</span>}
                      </label>

                      {/* TEXT, EMAIL, PHONE, URL fields */}
                      {['TEXT', 'EMAIL', 'PHONE', 'URL'].includes(field.type) && (
                        <div className="relative">
                          {field.type === 'EMAIL' && (
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          )}
                          {field.type === 'PHONE' && (
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          )}
                          {field.type === 'URL' && (
                            <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          )}
                          <Input
                            type={field.type === 'EMAIL' ? 'email' : field.type === 'URL' ? 'url' : 'text'}
                            value={(customFieldValues[field.name] as string) || ''}
                            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                            className={`${['EMAIL', 'PHONE', 'URL'].includes(field.type) ? 'pl-10' : ''} ${customFieldErrors[field.name] ? 'border-red-500' : ''}`}
                            error={customFieldErrors[field.name]}
                          />
                        </div>
                      )}

                      {/* NUMBER field */}
                      {field.type === 'NUMBER' && (
                        <div className="relative">
                          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <Input
                            type="number"
                            value={(customFieldValues[field.name] as string) || ''}
                            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                            className={`pl-10 ${customFieldErrors[field.name] ? 'border-red-500' : ''}`}
                            error={customFieldErrors[field.name]}
                          />
                        </div>
                      )}

                      {/* DATE field */}
                      {field.type === 'DATE' && (
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <Input
                            type="date"
                            value={(customFieldValues[field.name] as string) || ''}
                            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                            className={`pl-10 ${customFieldErrors[field.name] ? 'border-red-500' : ''}`}
                            error={customFieldErrors[field.name]}
                          />
                        </div>
                      )}

                      {/* BOOLEAN field */}
                      {field.type === 'BOOLEAN' && (
                        <div>
                          <Checkbox
                            checked={Boolean(customFieldValues[field.name])}
                            onChange={(e) => handleCustomFieldChange(field.name, e.target.checked)}
                            label={field.label}
                          />
                          {customFieldErrors[field.name] && (
                            <p className="mt-1 text-sm text-red-400">{customFieldErrors[field.name]}</p>
                          )}
                        </div>
                      )}

                      {/* SELECT field */}
                      {field.type === 'SELECT' && (
                        <div>
                          <Select
                            options={
                              field.options?.map((option) => ({
                                value: option,
                                label: option,
                              })) || []
                            }
                            value={(customFieldValues[field.name] as string) || ''}
                            onChange={(value) => handleCustomFieldChange(field.name, value as string)}
                            placeholder={`Select ${field.label}...`}
                            clearable
                          />
                          {customFieldErrors[field.name] && (
                            <p className="mt-1 text-sm text-red-400">{customFieldErrors[field.name]}</p>
                          )}
                        </div>
                      )}

                      {/* MULTI_SELECT field */}
                      {field.type === 'MULTI_SELECT' && (
                        <div>
                          <Select
                            options={
                              field.options?.map((option) => ({
                                value: option,
                                label: option,
                              })) || []
                            }
                            value={(customFieldValues[field.name] as string[]) || []}
                            onChange={(value) => handleCustomFieldChange(field.name, value)}
                            placeholder={`Select ${field.label}...`}
                            multiple
                            clearable
                          />
                          {customFieldErrors[field.name] && (
                            <p className="mt-1 text-sm text-red-400">{customFieldErrors[field.name]}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Opted In */}
          <Checkbox
            checked={optedInValue}
            onChange={(e) => setValue('optedIn', e.target.checked)}
            label="Contact has opted in to receive messages"
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : isEditing ? (
                'Save Changes'
              ) : (
                'Create Contact'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ContactFormModal;

